import React, { useState, useEffect } from 'react';
import '../styles/ProgramPages.css';
import '../styles/benefits.css';
import '../styles/master.css';
import SEO from '../components/SEO';
import '../styles/participants.css';
import '../styles/learning-experience.css';
import '../styles/pricing.css';
import '../styles/faq.css';
import '../styles/cta-final.css';
import '../styles/PopupForm.css';
import '../styles/features.css';
import '../styles/confirmation-modal.css';
import programsData from '../data/programs.json';
import Footer from '../components/Footer';
import PopupForm from '../components/PopupForm';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

// TopBarLeadForm component
function TopBarLeadForm({ visible, onSubmit, onClose, submitting }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !mobile.trim() || !consent) {
      setError('Please fill all fields and accept the consent.');
      return;
    }
    setError('');
    onSubmit({ name, email, mobile, consent });
  };

  if (!visible) return null;
  return (
    <div className="topbar-lead-form">
      <div className="topbar-lead-form-cta">Register for our upcoming Workshop</div>
      <form onSubmit={handleSubmit} className="topbar-lead-form-inner">
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={submitting}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={submitting}
          required
        />
        <input
          type="tel"
          placeholder="Mobile"
          value={mobile}
          onChange={e => setMobile(e.target.value)}
          disabled={submitting}
          required
          pattern="[0-9]{10,15}"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
          <input
            type="checkbox"
            id="topbar-consent"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
            disabled={submitting}
            required
          />
          <label htmlFor="topbar-consent" style={{ cursor: 'pointer' }}>I allow to send me regular updates</label>
        </div>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Get Info'}
        </button>
        <button type="button" className="topbar-lead-form-close" onClick={onClose} aria-label="Close">×</button>
      </form>
      {error && <div className="topbar-lead-form-error">{error}</div>}
    </div>
  );
}

function gtag_report_conversion(url) {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-16782075468',
      event_callback: function () {
        if (url) {
          window.location = url;
        }
      }
    });
    return false;
  }
  return true;
}

function CopilotTraining() {
  const data = programsData.copilotTraining;
  const [userLocation, setUserLocation] = useState('IN');

  // Initialize location from querystring, sessionStorage, or default to 'IN'
  useEffect(() => {
    // Check querystring for location parameter
    const urlParams = new URLSearchParams(window.location.search);
    const locationParam = urlParams.get('location');
    
    if (locationParam) {
      // Validate that the location exists in pricing data
      if (data.pricing && data.pricing.locations && data.pricing.locations[locationParam.toUpperCase()]) {
        const validLocation = locationParam.toUpperCase();
        setUserLocation(validLocation);
        // Store in sessionStorage for future use
        sessionStorage.setItem('userLocation', validLocation);
      }
    } else {
      // Check sessionStorage if querystring doesn't have location
      const storedLocation = sessionStorage.getItem('userLocation');
      if (storedLocation && data.pricing && data.pricing.locations && data.pricing.locations[storedLocation]) {
        setUserLocation(storedLocation);
      }
      // Otherwise, default to 'IN' (already set in useState)
    }
  }, [data.pricing]);

  // Get location-based pricing
  const getLocationPricing = () => {
    if (data.pricing && data.pricing.locations && data.pricing.locations[userLocation]) {
      return data.pricing.locations[userLocation];
    }
    // Default to IN pricing
    return { current: '₹3,999', original: '₹7,999' };
  };

  const pricing = getLocationPricing();
  
  // Extract currency and amount for display
  let pricingCurrency = '', pricingAmount = '', pricingOriginalCurrency = '', pricingOriginalAmount = '';
  const currentMatch = pricing.current.match(/^(\D+)?(\d+(?:,\d{3})*(?:\.\d+)?)/);
  if (currentMatch) {
    pricingCurrency = currentMatch[1] || '';
    pricingAmount = currentMatch[2];
  }
  const origMatch = pricing.original.match(/^(\D+)?(\d+(?:,\d{3})*(?:\.\d+)?)/);
  if (origMatch) {
    pricingOriginalCurrency = origMatch[1] || '';
    pricingOriginalAmount = origMatch[2];
  }
      // Carousel state for Copilot feature highlights
      const [featureIndex, setFeatureIndex] = useState(0);
      const featureTiles = [
        {
          icon: '/images/icons/copilot-fast.svg',
          title: 'Code Smarter, Deliver Faster',
          desc: 'AI-Powered Suggestions, Docs, and Boilerplate — Done Instantly.'
        },
        {
          icon: '/images/icons/copilot-context.svg',
          title: 'Master Copilot’s Context-Aware Suggestions',
          desc: 'Master How Copilot Understands Code, Prompts, and Comments'
        },
        {
          icon: '/images/icons/copilot-curve.svg',
          title: 'Keep Your Skills Ahead of the Curve',
          desc: 'Maintain Your Edge in the Fast-Changing Software Industry'
        }
      ];
      // Auto-scroll feature every 5 seconds
      useEffect(() => {
        const interval = setInterval(() => {
          setFeatureIndex(i => (i === featureTiles.length - 1 ? 0 : i + 1));
        }, 5000);
        return () => clearInterval(interval);
      }, [featureTiles.length]);
    // Inline hero form state
    const [showInlineHeroForm, setShowInlineHeroForm] = useState(true);
    const [inlineName, setInlineName] = useState('');
    const [inlineEmail, setInlineEmail] = useState('');
    const [inlineMobile, setInlineMobile] = useState('');
    const [inlineConsent, setInlineConsent] = useState(false);
    const [inlineHeroSubmitting, setInlineHeroSubmitting] = useState(false);
    const [inlineHeroSubmitted, setInlineHeroSubmitted] = useState(false);
    const [inlineHeroError, setInlineHeroError] = useState('');
  // State for AI Replace Me cards animation
  const [visibleAICards, setVisibleAICards] = useState([]);
  
  // State for 5W Framework steps animation
  const [visible5WSteps, setVisible5WSteps] = useState([]);
  
  // State for Curriculum modules animation
  const [visibleCurriculumModules, setVisibleCurriculumModules] = useState([]);
  
  // Scroll-up chevron state and top bar lead form state
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showTopBarForm, setShowTopBarForm] = useState(false);
  const [topBarSubmitting, setTopBarSubmitting] = useState(false);
  const [topBarSubmitted, setTopBarSubmitted] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY || document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (pageHeight > 0 && scrolled > pageHeight * 0.10) {
        setShowScrollUp(true);
        if (window.innerWidth <= 900) {
          setShowTopBarForm(true);
        }
      } else {
        setShowScrollUp(false);
        setShowTopBarForm(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // ...existing code...
  const [openFaq, setOpenFaq] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formType, setFormType] = useState('');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    message: '',
    title: ''
  });
  const [showChaiChat, setShowChaiChat] = useState(false);
  const [chaiMessages, setChaiMessages] = useState([]);
  const [chaiInput, setChaiInput] = useState('');
  const [chaiStep, setChaiStep] = useState('initial'); // initial -> pricing_shared -> collecting_name -> collecting_email -> collecting_mobile -> completed
  const [chaiLeadData, setChaiLeadData] = useState({ name: '', email: '', mobile: '' });
  const copilotChatPrompts = [
    { label: 'Price & seats', value: 'What is the current price and seat limit?' },
    { label: 'Join live', value: 'Help me join the next live session.' },
    { label: 'Course outline', value: 'Can you share the course outline?' },
    { label: 'Recordings & cert', value: 'Do I get recordings and a certificate?' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Get location from sessionStorage
    const storedLocation = sessionStorage.getItem('loc');
    if (storedLocation) {
      setUserLocation(storedLocation);
    }
  }, []);

  // Seed Ask Chai conversation with program context
  useEffect(() => {
    setShowChaiChat(true);
    setChaiMessages((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          type: 'bot',
          text: `Hi! I'm Chai, your Copilot guide. 👋\n\nWant the price and seats? I can share that, then help you register.`,
          timestamp: new Date().toISOString()
        }
      ];
    });
  }, [pricing.current, pricing.original]);

  // Intersection Observer for AI Replace Me cards animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.cardindex);
            setVisibleAICards((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cardElements = document.querySelectorAll('.ai-replace-card');
    cardElements.forEach((el) => observer.observe(el));

    return () => {
      cardElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Intersection Observer for 5W Framework steps animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.stepindex);
            setVisible5WSteps((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.3 }
    );

    const stepElements = document.querySelectorAll('.framework-5w-step');
    stepElements.forEach((el) => observer.observe(el));

    return () => {
      stepElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Intersection Observer for curriculum modules animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.moduleindex);
            setVisibleCurriculumModules((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const moduleElements = document.querySelectorAll('.curriculum-module-card');
    moduleElements.forEach((el) => observer.observe(el));

    return () => {
      moduleElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const openPopup = (type) => {
    setFormType(type);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const showConfirmation = (type) => {
    const messages = {
      live_session: {
        title: "Registration Successful!",
        message: "Thank you for registering for our next live session. We'll send you the session details via email."
      },
      course_outline: {
        title: "Course Outline Sent!",
        message: "The course outline has been sent to your email. Check your inbox shortly."
      }
    };
    
    setConfirmationModal({
      isOpen: true,
      title: messages[type].title,
      message: messages[type].message
    });

    // Auto close after 3 seconds
    setTimeout(() => {
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
    }, 3000);
  };

  const closeConfirmation = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleChaiResponse = async (rawInput) => {
    const input = rawInput.toLowerCase();

    if (chaiStep === 'initial') {
      setChaiStep('pricing_shared');
      setChaiMessages(prev => [...prev, {
        type: 'bot',
        text: `The GitHub Copilot Training is ${pricing.current} (was ${pricing.original}). Seats are limited to 20 per batch.\n\nWant me to save you a seat? What is your name?`,
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    if (chaiStep === 'pricing_shared') {
      setChaiLeadData(prev => ({ ...prev, name: rawInput.trim() }));
      setChaiStep('collecting_email');
      setChaiMessages(prev => [...prev, {
        type: 'bot',
        text: `Great to meet you, ${rawInput.trim()}! What's the best email to send the session link and outline?`,
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    if (chaiStep === 'collecting_email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(rawInput.trim())) {
        setChaiMessages(prev => [...prev, {
          type: 'bot',
          text: 'That email looks off. Please enter a valid email (e.g., name@example.com).',
          timestamp: new Date().toISOString()
        }]);
        return;
      }
      setChaiLeadData(prev => ({ ...prev, email: rawInput.trim() }));
      setChaiStep('collecting_mobile');
      setChaiMessages(prev => [...prev, {
        type: 'bot',
        text: 'Thanks! Lastly, what is your mobile number?',
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    if (chaiStep === 'collecting_mobile') {
      const mobile = rawInput.trim();
      setChaiLeadData(prev => ({ ...prev, mobile }));
      setChaiStep('submitting');

      setChaiMessages(prev => [...prev, {
        type: 'bot',
        text: 'Saving your spot... ⏳',
        timestamp: new Date().toISOString()
      }]);

      try {
        let token = localStorage.getItem('token');
        if (!token) {
          token = generateToken();
          localStorage.setItem('token', token);
        }
        const headers = {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: token
        };
        const body = {
          name: chaiLeadData.name,
          email: chaiLeadData.email,
          mobile,
          consent: true,
          source: 'copilot_chai_chat',
          interestedIn: 'copilot_training'
        };
        const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error('Lead save failed');
        setChaiStep('completed');
        setChaiMessages(prev => [...prev, {
          type: 'bot',
          text: `All set! You're on the list for the next live Copilot session at ${pricing.current}. Want me to open the registration form now?`,
          timestamp: new Date().toISOString()
        }]);
      } catch (err) {
        console.error('Chai lead submit error:', err);
        setChaiStep('pricing_shared');
        setChaiMessages(prev => [...prev, {
          type: 'bot',
          text: 'Hmm, saving failed. Can we retry? Please confirm your name again.',
          timestamp: new Date().toISOString()
        }]);
      }
      return;
    }

    // Completed or general Q&A fallback
    let response = "I can share pricing, seats, and help you register. Want me to open the live session form?";
    if (input.includes('form') || input.includes('register') || input.includes('seat') || input.includes('join')) {
      response = 'Opening the live session form for you.';
      setTimeout(() => openPopup('live_session'), 120);
    } else if (input.includes('outline') || input.includes('curriculum')) {
      response = 'Opening the course outline request form.';
      setTimeout(() => openPopup('course_outline'), 120);
    }
    setChaiMessages(prev => [...prev, { type: 'bot', text: response, timestamp: new Date().toISOString() }]);
  };

  const handleChaiQuickPrompt = (prompt) => {
    setChaiMessages(prev => [...prev, { type: 'user', text: prompt, timestamp: new Date().toISOString() }]);
    handleChaiResponse(prompt);
  };

  const handleChaiSubmit = (e) => {
    e.preventDefault();
    if (!chaiInput.trim()) return;
    const trimmed = chaiInput.trim();
    setChaiMessages(prev => [...prev, { type: 'user', text: trimmed, timestamp: new Date().toISOString() }]);
    setChaiInput('');
    handleChaiResponse(trimmed);
  };

  // Top bar form submit handler
  const handleTopBarFormSubmit = async ({ name, email, mobile, consent }) => {
    setTopBarSubmitting(true);
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        token = generateToken();
        localStorage.setItem('token', token);
      }
      const headers = {
        'Content-Type': 'application/json',
        [TOKEN_HEADER]: token
      };
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          email,
          mobile,
          consent,
          source: 'copilot_training_topbar',
          interestedIn: 'copilot_training'
        })
      });
      if (!response.ok) throw new Error('Failed to submit');
      setTopBarSubmitted(true);
      setTimeout(() => {
        setShowTopBarForm(false);
        setTopBarSubmitted(false);
      }, 2500);
    } catch (e) {
      alert('Failed to submit. Please try again.');
    } finally {
      setTopBarSubmitting(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'copilot_training_page',
          interestedIn: formType === 'live_session' ? 'copilot_training' : 'training_info'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      closePopup();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Is this course live or recorded?",
      answer: "This is a live, interactive course where you'll get real-time guidance and feedback."
    },
    {
      question: "Do I need prior experience?",
      answer: "No prior experience with GitHub Copilot is required, but basic programming knowledge is recommended."
    },
    {
      question: "Will I get a certificate?",
      answer: "Yes, signed by a GitHub Copilot Certified Trainer."
    },
    {
      question: "What if I miss a session?",
      answer: "Recordings will be available for all registered participants."
    }
  ];

  // Import testimonials
  const testimonials = require('../data/testimonials.json').filter(t => t.program === 'copilot');

  return (
    <div className="copilot-page" style={{ position: 'relative' }}>
      <SEO pageName="copilotTraining" />
      <section className="hero-section copilot-hero" style={{ position: 'relative', backgroundImage: 'url(/images/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', width: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 1 }}></div>
        <div className="hero-content responsive-hero-content" style={{ display: 'flex', alignItems: 'center', gap: '2.5rem', position: 'relative', zIndex: 2 }}>
          <div className="hero-text" style={{ flex: 2}}>
            <h1>Don't Just Co-Exist with AI. Command It.</h1>
            <p className="hero-subtitle">The era of manual coding is ending. Join the elite 1% of developers using the 5W Framework to architect solutions faster, debug instantly, and secure their careers.</p>
            <div className="cta-buttons">
              <button
                onClick={() => { gtag_report_conversion(); openPopup('live_session'); }}
                className="button button-primary"
              >
                Join the Masterclass - {pricingCurrency}{pricingAmount}
              </button>
            </div>
            <p style={{ marginTop: '12px', fontSize: '0.95rem', color: '#ffd700', fontWeight: 600 }}>
              Live Workshop | Limited to 20 Seats per Batch
            </p>
            {/* Inline Join Live Session Form */}
            {showInlineHeroForm && (
              <div className="inline-hero-lead-form" style={{ marginTop: 18, borderRadius: 10, boxShadow: '0 2px 12px rgba(44,62,80,0.10)', padding: 18, maxWidth: 380 }}>
                <form
                  onSubmit={async e => {
                    e.preventDefault();
                    if (!inlineName.trim() || !inlineEmail.trim() || !inlineMobile.trim() || !inlineConsent) {
                      setInlineHeroError('Please fill all fields and accept the consent.');
                      return;
                    }
                    setInlineHeroError('');
                    setInlineHeroSubmitting(true);
                    try {
                      let token = localStorage.getItem('token');
                      if (!token) {
                        token = generateToken();
                        localStorage.setItem('token', token);
                      }
                      const headers = {
                        'Content-Type': 'application/json',
                        [TOKEN_HEADER]: token
                      };
                      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                          name: inlineName,
                          email: inlineEmail,
                          mobile: inlineMobile,
                          consent: inlineConsent,
                          source: 'copilot_training_hero_inline',
                          interestedIn: 'copilot_training'
                        })
                      });
                      if (!response.ok) throw new Error('Failed to submit');
                      setInlineHeroSubmitted(true);
                      setTimeout(() => {
                        setShowInlineHeroForm(false);
                        setInlineHeroSubmitted(false);
                        setInlineName('');
                        setInlineEmail('');
                        setInlineMobile('');
                        setInlineConsent(false);
                      }, 2500);
                    } catch (e) {
                      setInlineHeroError('Failed to submit. Please try again.');
                    } finally {
                      setInlineHeroSubmitting(false);
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="inline-hero-form-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={inlineName}
                        onChange={e => setInlineName(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        required
                        style={{ flex: '1 1 120px', padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 100 }}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={inlineEmail}
                        onChange={e => setInlineEmail(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        required
                        style={{ flex: '1 1 120px', padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 100 }}
                      />
                      <input
                        type="tel"
                        placeholder="Mobile"
                        value={inlineMobile}
                        onChange={e => setInlineMobile(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        required
                        pattern="[0-9]{10,15}"
                        style={{ flex: '1 1 100px', padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 90 }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                      <input
                        type="checkbox"
                        id="inline-consent"
                        checked={inlineConsent}
                        onChange={e => setInlineConsent(e.target.checked)}
                        disabled={inlineHeroSubmitting}
                        required
                      />
                      <label htmlFor="inline-consent" style={{ cursor: 'pointer' }}>I allow to send me regular updates</label>
                    </div>
                    <button
                      type="submit"
                      disabled={inlineHeroSubmitting}
                      style={{ background: '#DA291C', color: '#fff', border: 'none', borderRadius: 5, padding: '10px 18px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {inlineHeroSubmitting ? 'Submitting...' : 'Join Now'}
                    </button>
                    {inlineHeroError && <div style={{ color: '#DA291C', fontSize: '0.95rem' }}>{inlineHeroError}</div>}
                    {inlineHeroSubmitted && <div style={{ color: 'green', fontWeight: 600 }}>Thank you! We'll be in touch soon.</div>}
                  </div>
                </form>
              </div>
            )}
           
            
            <p className="stats-text" dangerouslySetInnerHTML={{ __html: data.hero.statsText }} />
          </div>
          <div className="hero-image responsive-hero-image" style={{ flex: '0 0 340px', textAlign: 'right' }}>
            <img 
              src="/images/ghc.jpg" 
              alt="Developer working with GitHub Copilot"
              className="rounded-image"
              style={{ maxWidth: '340px', width: '100%', height: 'auto', borderRadius: '18px', boxShadow: '0 4px 24px rgba(44,62,80,0.10)' }}
            />
          </div>
        </div>
      </section>

      {/* Will AI Replace Me Section */}
      <section style={{ background: '#f8f9fa', padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontWeight: 700, fontSize: '2.5rem', marginBottom: '1.5rem', color: 'rgb(218, 41, 28)' }}>
            Will AI Replace Me?
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#444', marginBottom: '2rem', lineHeight: '1.8' }}>
            Not if you master it. The industry is splitting into two groups:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem', textAlign: 'left' }}>
            <div 
              className="ai-replace-card"
              data-cardindex="0"
              style={{ 
                background: '#fff', 
                padding: '2rem', 
                borderRadius: '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                opacity: visibleAICards.includes(0) ? 1 : 0,
                transform: visibleAICards.includes(0) ? 'perspective(1000px) rotateY(0deg)' : 'perspective(1000px) rotateY(-90deg)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
              }}>
              <h3 style={{ color: '#DA291C', fontWeight: 700, fontSize: '1.5rem', marginBottom: '1rem' }}>
                The Obsolete:
              </h3>
              <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.7' }}>
                Devs who treat Copilot like a fancy autocomplete. They are getting faster, but they are making more mistakes.
              </p>
            </div>
            <div 
              className="ai-replace-card"
              data-cardindex="1"
              style={{ 
                background: '#fff', 
                padding: '2rem', 
                borderRadius: '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                opacity: visibleAICards.includes(1) ? 1 : 0,
                transform: visibleAICards.includes(1) ? 'perspective(1000px) rotateY(0deg)' : 'perspective(1000px) rotateY(90deg)',
                transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s'
              }}>
              <h3 style={{ color: '#28a745', fontWeight: 700, fontSize: '1.5rem', marginBottom: '1rem' }}>
                The Architects:
              </h3>
              <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.7' }}>
                Devs who use AI to handle the grunt work so they can focus on System Design, Logic, and Complex Problem Solving.
              </p>
            </div>
          </div>
          <p style={{ fontSize: '1.2rem', fontWeight: 600, color: '#222', marginTop: '2rem' }}>
            This workshop is designed to pull you out of the first group and put you in the second.
          </p>
        </div>
      </section>

      {/* 5W Framework Section */}
      <section style={{ background: '#fff', padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: '2.5rem', marginBottom: '1rem', color: '#222' }}>
            Stop "Chatting" with Code. Start Engineering It.
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.15rem', color: '#444', marginBottom: '3rem', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto 3rem' }}>
            Most devs get average results because they write average prompts. Chaitanya's proprietary <strong>5W Framework</strong> adapts standard engineering requirements into AI commands:
          </p>
          
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative' }}>
            {/* Vertical connecting line */}
            <div style={{ position: 'absolute', left: '40px', top: '80px', bottom: '80px', width: '3px', background: 'linear-gradient(to bottom, #DA291C, #ff6b6b)', zIndex: 0 }}></div>
            
            {/* WHO Step */}
            <div 
              className="framework-5w-step"
              data-stepindex="0"
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '30px', 
                marginBottom: '50px', 
                position: 'relative', 
                zIndex: 1,
                opacity: visible5WSteps.includes(0) ? 1 : 0,
                transform: visible5WSteps.includes(0) ? 'translateX(0)' : 'translateX(-100px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
              }}>
              <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff', border: '4px solid #DA291C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(218, 41, 28, 0.2)', fontSize: '1.8rem', fontWeight: '700', color: '#DA291C' }}>
                  W
                </div>
                <div style={{ background: '#DA291C', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '0.9rem', fontWeight: '700' }}>WHO</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 3px 15px rgba(0,0,0,0.08)', border: '2px solid #f0f0f0' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#DA291C', marginBottom: '12px' }}>WHO: Contextualize the Stack</h3>
                <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.6', margin: 0 }}>Contextualize the stack (React/Node/Postgres) & constraints.</p>
              </div>
            </div>
            
            {/* WHAT Step */}
            <div 
              className="framework-5w-step"
              data-stepindex="1"
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '30px', 
                marginBottom: '50px', 
                position: 'relative', 
                zIndex: 1,
                opacity: visible5WSteps.includes(1) ? 1 : 0,
                transform: visible5WSteps.includes(1) ? 'translateX(0)' : 'translateX(-100px)',
                transition: 'opacity 0.6s ease-out 0.15s, transform 0.6s ease-out 0.15s'
              }}>
              <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff', border: '4px solid #DA291C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(218, 41, 28, 0.2)', fontSize: '1.8rem', fontWeight: '700', color: '#DA291C' }}>
                  W
                </div>
                <div style={{ background: '#DA291C', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '0.9rem', fontWeight: '700' }}>WHAT</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 3px 15px rgba(0,0,0,0.08)', border: '2px solid #f0f0f0' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#DA291C', marginBottom: '12px' }}>WHAT: Define Precise Logic</h3>
                <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.6', margin: 0 }}>Define precise functional logic (no hallucinations).</p>
              </div>
            </div>
            
            {/* WHERE Step */}
            <div 
              className="framework-5w-step"
              data-stepindex="2"
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '30px', 
                marginBottom: '50px', 
                position: 'relative', 
                zIndex: 1,
                opacity: visible5WSteps.includes(2) ? 1 : 0,
                transform: visible5WSteps.includes(2) ? 'translateX(0)' : 'translateX(-100px)',
                transition: 'opacity 0.6s ease-out 0.30s, transform 0.6s ease-out 0.30s'
              }}>
              <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff', border: '4px solid #DA291C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(218, 41, 28, 0.2)', fontSize: '1.8rem', fontWeight: '700', color: '#DA291C' }}>
                  W
                </div>
                <div style={{ background: '#DA291C', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '0.9rem', fontWeight: '700' }}>WHERE</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 3px 15px rgba(0,0,0,0.08)', border: '2px solid #f0f0f0' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#DA291C', marginBottom: '12px' }}>WHERE: Control the Scope</h3>
                <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.6', margin: 0 }}>Scope control (File-level vs. Repo-level).</p>
              </div>
            </div>
            
            {/* WHEN Step */}
            <div 
              className="framework-5w-step"
              data-stepindex="3"
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '30px', 
                marginBottom: '50px', 
                position: 'relative', 
                zIndex: 1,
                opacity: visible5WSteps.includes(3) ? 1 : 0,
                transform: visible5WSteps.includes(3) ? 'translateX(0)' : 'translateX(-100px)',
                transition: 'opacity 0.6s ease-out 0.45s, transform 0.6s ease-out 0.45s'
              }}>
              <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff', border: '4px solid #DA291C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(218, 41, 28, 0.2)', fontSize: '1.8rem', fontWeight: '700', color: '#DA291C' }}>
                  W
                </div>
                <div style={{ background: '#DA291C', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '0.9rem', fontWeight: '700' }}>WHEN</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 3px 15px rgba(0,0,0,0.08)', border: '2px solid #f0f0f0' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#DA291C', marginBottom: '12px' }}>WHEN: Trigger-Based Logic</h3>
                <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.6', margin: 0 }}>Trigger-based logic generation.</p>
              </div>
            </div>
            
            {/* WHY Step */}
            <div 
              className="framework-5w-step"
              data-stepindex="4"
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '30px', 
                marginBottom: '50px', 
                position: 'relative', 
                zIndex: 1,
                opacity: visible5WSteps.includes(4) ? 1 : 0,
                transform: visible5WSteps.includes(4) ? 'translateX(0)' : 'translateX(-100px)',
                transition: 'opacity 0.6s ease-out 0.60s, transform 0.6s ease-out 0.60s'
              }}>
              <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fff', border: '4px solid #DA291C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(218, 41, 28, 0.2)', fontSize: '1.8rem', fontWeight: '700', color: '#DA291C' }}>
                  W
                </div>
                <div style={{ background: '#DA291C', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '0.9rem', fontWeight: '700' }}>WHY</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 3px 15px rgba(0,0,0,0.08)', border: '2px solid #f0f0f0' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#DA291C', marginBottom: '12px' }}>WHY: Optimize Your Goals</h3>
                <p style={{ fontSize: '1.05rem', color: '#555', lineHeight: '1.6', margin: 0 }}>Optimization goals (Performance vs. Readability).</p>
              </div>
            </div>
          </div>
          
          <div style={{ background: 'linear-gradient(135deg, #DA291C 0%, #a01f15 100%)', padding: '2.5rem', borderRadius: '12px', textAlign: 'center', color: '#fff', marginTop: '3rem', boxShadow: '0 4px 20px rgba(218, 41, 28, 0.3)' }}>
            <h3 style={{ fontWeight: 700, fontSize: '1.6rem', marginBottom: '0.75rem', color: '#fff' }}>
              The Result:
            </h3>
            <p style={{ fontSize: '1.2rem', lineHeight: '1.7', color: '#fff', margin: 0 }}>
              Clean, production-ready code that passes code review on the first try.
            </p>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section style={{ background: '#f8f9fa', padding: '60px 0' }}>
        <div className="container">
          <h2 style={{ textAlign: 'center', marginBottom: '2.5rem', color: '#DA291C', fontWeight: 700, fontSize: '2.5rem' }}>The Curriculum</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '32px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Module 1 */}
            <div
              className="curriculum-module-card"
              data-moduleindex="0"
              style={{
                background: '#DA291C',
                color: '#fff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
                padding: '28px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                opacity: visibleCurriculumModules.includes(0) ? 1 : 0,
                transform: visibleCurriculumModules.includes(0) ? 'perspective(1000px) rotateX(0deg)' : 'perspective(1000px) rotateX(-90deg)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out'
              }}
            >
              <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem', marginBottom: '8px' }}>Module 1</div>
              <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '8px', color: '#fff' }}>Configuration & Security</h3>
              <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', color: '#fff', fontSize: '1rem' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>Setup for Enterprise: Privacy settings, codebase indexing, and "context" management.</span>
                </li>
              </ul>
            </div>

            {/* Module 2 */}
            <div
              className="curriculum-module-card"
              data-moduleindex="1"
              style={{
                background: '#fff',
                color: '#222',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
                padding: '28px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                border: '2px solid #e8e8e8',
                opacity: visibleCurriculumModules.includes(1) ? 1 : 0,
                transform: visibleCurriculumModules.includes(1) ? 'perspective(1000px) rotateX(0deg)' : 'perspective(1000px) rotateX(-90deg)',
                transition: 'opacity 0.7s ease-out 0.15s, transform 0.7s ease-out 0.15s'
              }}
            >
              <div style={{ fontWeight: 700, color: '#DA291C', fontSize: '1.1rem', marginBottom: '8px' }}>Module 2</div>
              <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '8px', color: '#222' }}>The 5W Framework in Action</h3>
              <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', color: '#222', fontSize: '1rem' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#DA291C', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>Live coding: We take a complex JIRA ticket and solve it in 10 minutes using 5W prompts.</span>
                </li>
              </ul>
            </div>

            {/* Module 3 */}
            <div
              className="curriculum-module-card"
              data-moduleindex="2"
              style={{
                background: '#fff',
                color: '#222',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
                padding: '28px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                border: '2px solid #e8e8e8',
                opacity: visibleCurriculumModules.includes(2) ? 1 : 0,
                transform: visibleCurriculumModules.includes(2) ? 'perspective(1000px) rotateX(0deg)' : 'perspective(1000px) rotateX(-90deg)',
                transition: 'opacity 0.7s ease-out 0.30s, transform 0.7s ease-out 0.30s'
              }}
            >
              <div style={{ fontWeight: 700, color: '#DA291C', fontSize: '1.1rem', marginBottom: '8px' }}>Module 3</div>
              <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '8px', color: '#222' }}>Advanced Refactoring & Testing</h3>
              <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', color: '#222', fontSize: '1rem' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#DA291C', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>Legacy Code Revival: Using Copilot to explain and refactor "spaghetti code."</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#DA291C', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>Test Driven Development (TDD): Generating Unit and Integration tests before writing code.</span>
                </li>
              </ul>
            </div>

            {/* Module 4 */}
            <div
              className="curriculum-module-card"
              data-moduleindex="3"
              style={{
                background: '#DA291C',
                color: '#fff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
                padding: '28px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                opacity: visibleCurriculumModules.includes(3) ? 1 : 0,
                transform: visibleCurriculumModules.includes(3) ? 'perspective(1000px) rotateX(0deg)' : 'perspective(1000px) rotateX(-90deg)',
                transition: 'opacity 0.7s ease-out 0.45s, transform 0.7s ease-out 0.45s'
              }}
            >
              <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem', marginBottom: '8px' }}>Module 4</div>
              <h3 style={{ fontWeight: 700, fontSize: '1.3rem', marginBottom: '8px', color: '#fff' }}>Certification & Future Proofing</h3>
              <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', color: '#fff', fontSize: '1rem' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>Official Microsoft/GitHub Certification prep roadmap.</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1em', display: 'inline-block', minWidth: '20px' }}>→</span>
                  <span>How to put "AI-Native Developer" on your resume effectively.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Copilot Feature Highlights Section (attached) */}
      <section style={{ background: '#fff', padding: '48px 0 32px 0' }}>
        <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: '2.2rem', marginBottom: '0.5rem', color: '#222' }}>
          Code Smarter, Faster, and Better — <span style={{ color: '#DA291C' }}>Learn GitHub Copilot</span>
        </h2>
        <p style={{ textAlign: 'center', fontSize: '1.15rem', color: '#444', marginBottom: '2.5rem' }}>
          The AI Era is Here — Elevate Your Development Skills Now
        </p>
        <div className="copilot-feature-slider" style={{ maxWidth: 400, margin: '0 auto' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '18px',
              boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
              padding: '32px',
              minWidth: '220px',
              maxWidth: '360px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              transition: 'all 0.3s',
            }}
          >
            <img src={featureTiles[featureIndex].icon} alt={featureTiles[featureIndex].title} style={{ width: '64px', height: '64px', marginBottom: '18px', display: 'block' }} />
            <h3 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '12px', color: '#222' }}>{featureTiles[featureIndex].title}</h3>
            <p style={{ color: '#444', fontSize: '1rem' }}>{featureTiles[featureIndex].desc}</p>
          </div>
        </div>
      </section>

      {/* Career ROI Stack Section */}
      <section style={{ background: '#fff', padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: '2.5rem', marginBottom: '1rem', color: '#222' }}>
            More Than Just a Workshop
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', color: '#DA291C', fontWeight: 600, marginBottom: '3rem' }}>
            The "Career ROI" Stack (Justifying the Price)
          </p>
          
          <div style={{ background: '#f8f9fa', borderRadius: '16px', padding: '3rem 2.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '1.5rem', color: '#28a745', fontWeight: 700, minWidth: '30px' }}>✅</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '0.25rem' }}>4-Hour Live Intensive</h3>
                  <p style={{ fontSize: '1rem', color: '#555', margin: 0 }}>No fluff, just tactics</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '1.5rem', color: '#28a745', fontWeight: 700, minWidth: '30px' }}>✅</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '0.25rem' }}>The "5W Prompt Library" for Developers</h3>
                  <p style={{ fontSize: '1rem', color: '#555', margin: 0 }}>Copy-paste prompts for Refactoring, SQL, Regex, & React</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '1.5rem', color: '#28a745', fontWeight: 700, minWidth: '30px' }}>✅</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '0.25rem' }}>Certification Readiness Guide</h3>
                  </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '1.5rem', color: '#28a745', fontWeight: 700, minWidth: '30px' }}>✅</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '0.25rem' }}>Recording Access</h3>
                  <p style={{ fontSize: '1rem', color: '#555', margin: 0 }}>Lifetime reference</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '1.5rem', color: '#28a745', fontWeight: 700, minWidth: '30px' }}>✅</span>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '0.25rem' }}>Money-Back Guarantee</h3>
                  <p style={{ fontSize: '1rem', color: '#555', margin: 0 }}>If you don't find this useful after Module 3, we refund you.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Copilot Testimonials Section (moved before pricing) */}
      <section className="testimonials-section">
        <div className="container">
          <h2 style={{ textAlign: 'center', color: '#DA291C', fontWeight: 700 }}>What Our Copilot Learners Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className="testimonial-card fade-in"
                style={{ animationDelay: `${i * 0.2}s` }}
              >
                <p>{t.quote}</p>
                <h4>{t.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="master-section">
        <div className="container">
          <h2>Here's What <span className="highlight">You'll Master</span></h2>
          <p className="subtitle">{data.whatYoullMaster.subtitle}</p>
          
          <div className="master-grid">
            {data.whatYoullMaster.topics.map((topic, index) => (
              <div key={index} className="master-card">
                <div className="master-icon">
                  <img src={topic.icon} alt={topic.title} />
                </div>
                <div className="master-content">
                  <h3>{topic.title}</h3>
                  <p>{topic.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bonus-section">
            {data.whatYoullMaster.bonuses.map((bonus, index) => (
              <div key={index} className="bonus-item">
                <span className="check-icon">✓</span>
                <p>
                  {bonus.isBonus && <strong>Bonus: </strong>}
                  {bonus.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="who-should-join">
        <div className="container">
          <h2>Who Should <span className="highlight">Join This Program?</span></h2>
          <div className="participants-grid">
            {data.whoShouldJoin.participants.map((participant, index) => (
              <div key={index} className="participant-card">
                <div className="participant-icon">
                  <img src={participant.icon} alt={participant.title} />
                </div>
                <h3>{participant.title}</h3>
                <p>{participant.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="learning-experience">
        <div className="container">
          <h2>Your Learning <span className="highlight">Experience</span></h2>
          <div className="learning-steps">
            {data.learningExperience.features.map((step, index) => (
              <div key={index} className="learning-step">
                <img src={step.icon} alt={step.title} />
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      <section className="pricing-section">
        <div className="container">
          <h2>Pricing & <span className="highlight">Enrollment</span></h2>
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-amount">
                <span className="original-price">{pricingOriginalCurrency}{pricingOriginalAmount}</span>
                <span className="current-price">{pricingCurrency}{pricingAmount}</span>
              </div>
              <p className="pricing-save">(Save 60%)</p>
            </div>
            <div className="pricing-features">
              <div>live sessions</div>
              <div>resources</div>
              <div>completion certificate</div>
            </div>
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('live_session'); }} 
              className="enroll-button"
            >
              Enroll Now
            </button>
            <p className="pricing-note">Only 50 seats per session.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked <span className="highlight">Questions</span></h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className="faq-question" 
                  onClick={() => toggleFaq(index)}
                >
                  {faq.question}
                  <span className={`faq-toggle ${openFaq === index ? 'open' : ''}`}>
                    +
                  </span>
                </button>
                <div className={`faq-answer ${openFaq === index ? 'open' : ''}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-final">
        <div className="container">
          <h2>Start Building with AI. Lead with Tech.</h2>
          <p>Join the next live GitHub Copilot session and learn how to code faster, smarter, and better.</p>
          <div className="cta-buttons">
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('live_session'); }} 
              className="cta-button cta-button-secondary"
            >
              Join the Next Live Session
            </button>
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('course_outline'); }} 
              className="cta-button cta-button-primary"
            >
              Download Course Outline
            </button>
          </div>
        </div>
      </section>

      {/* Ask Chai Chatbot (Copilot Program) */}
      <div
        style={{
          position: 'fixed',
          bottom: showTopBarForm && window.innerWidth <= 900 ? '110px' : '28px',
          left: '24px',
          zIndex: 1250,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        {!showChaiChat && (
          <button
            onClick={() => setShowChaiChat(true)}
            style={{
              background: 'linear-gradient(135deg, #DA291C 0%, #b81f14 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '48px',
              padding: '14px 18px',
              fontSize: '0.98rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 6px 22px rgba(218, 41, 28, 0.35)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            aria-label="Open Ask Chai chatbot for Copilot Training"
          >
            <span style={{ fontSize: '1.2rem' }}>☕</span>
            Ask Chai about Copilot
          </button>
        )}

        {showChaiChat && (
          <div
            style={{
              width: '360px',
              height: '520px',
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 10px 32px rgba(0, 0, 0, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #DA291C 0%, #b81f14 100%)',
                color: '#fff',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}>☕</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Ask Chai</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>Copilot Training Concierge</p>
                </div>
              </div>
              <button
                onClick={() => setShowChaiChat(false)}
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  border: 'none',
                  color: '#fff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
                aria-label="Close Ask Chai chatbot"
              >
                ×
              </button>
            </div>

            <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px', background: '#fff' }}>
              {copilotChatPrompts.map((prompt) => (
                <button
                  key={prompt.value}
                  onClick={() => handleChaiQuickPrompt(prompt.value)}
                  style={{
                    border: '1px solid #DA291C',
                    background: '#fff',
                    color: '#DA291C',
                    borderRadius: '20px',
                    padding: '6px 10px',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  {prompt.label}
                </button>
              ))}
            </div>

            <div
              style={{
                flex: 1,
                padding: '14px',
                background: '#f8f9fa',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              {chaiMessages.map((message, index) => (
                <div
                  key={`${message.timestamp}-${index}`}
                  style={{
                    display: 'flex',
                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div
                    style={{
                      maxWidth: '78%',
                      padding: '10px 12px',
                      borderRadius: message.type === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: message.type === 'user' ? '#DA291C' : '#fff',
                      color: message.type === 'user' ? '#fff' : '#1f2933',
                      fontSize: '0.95rem',
                      lineHeight: 1.5,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {message.text}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleChaiSubmit} style={{ padding: '12px', borderTop: '1px solid #ececec', background: '#fff', display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={chaiInput}
                onChange={(e) => setChaiInput(e.target.value)}
                placeholder="Ask about Copilot..."
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '12px',
                  border: '1px solid #ddd',
                  fontSize: '0.95rem'
                }}
              />
              <button
                type="submit"
                style={{
                  background: '#DA291C',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Send
              </button>
            </form>
          </div>
        )}
      </div>


      <PopupForm 
        isOpen={isPopupOpen} 
        onClose={closePopup} 
        formType={formType}
        onSubmit={async (formData) => {
          try {
            await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...formData,
                source: 'copilot_training_page',
                interestedIn: formType === 'live_session' ? 'copilot_training' : 'training_info'
              })
            });
            closePopup();
            showConfirmation(formType);
          } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit form. Please try again.');
          }
        }}
      />


      {/* Scroll Up Chevron */}
      {showScrollUp && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: '32px',
            bottom: showTopBarForm && window.innerWidth <= 900 ? '90px' : '40px',
            zIndex: 1300,
            background: '#fff',
            border: '2px solid #DA291C',
            borderRadius: '50%',
            width: '54px',
            height: '54px',
            boxShadow: '0 2px 12px rgba(44,62,80,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            opacity: 0.95
          }}
          aria-label="Scroll to top"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="16" fill="#fff" />
            <path d="M10 18l6-6 6 6" stroke="#DA291C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </button>
      )}

      {/* Confirmation Modal */}
      {confirmationModal.isOpen && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <button className="close-button" onClick={closeConfirmation}>&times;</button>
            <h3>{confirmationModal.title}</h3>
            <p>{confirmationModal.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CopilotTraining;
// TopBarLeadForm styles will be added in ProgramPages.css